import { useEffect, useState } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Plus, PlayCircle, CheckCircle, Clock, XCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { StartRunDialog } from "@/components/run/StartRunDialog";
import { ListPageSkeleton } from "@/components/ui/skeleton-card";
import { EmptyState, ErrorState } from "@/components/ui/empty-state";
import { useToast } from "@/hooks/use-toast";

interface OutletContext {
  user: any;
  organizationId: string;
}

interface Run {
  id: string;
  name: string;
  status: string;
  started_at: string | null;
  due_at: string | null;
  completed_at: string | null;
  sop_template: {
    title: string;
  };
  steps?: { status: string }[];
}

const Runs = () => {
  const { user, organizationId } = useOutletContext<OutletContext>();
  const [runs, setRuns] = useState<Run[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isStartDialogOpen, setIsStartDialogOpen] = useState(false);
  const { toast } = useToast();

  const fetchRuns = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from("sop_runs")
        .select(`
          *,
          sop_template:sop_templates (
            title
          )
        `)
        .eq("organization_id", organizationId)
        .order("created_at", { ascending: false });

      if (fetchError) throw fetchError;

      // Fetch step counts for progress calculation
      const runsWithProgress = await Promise.all(
        (data || []).map(async (run) => {
          const { data: steps } = await supabase
            .from("sop_run_steps")
            .select("status")
            .eq("sop_run_id", run.id);
          return { ...run, steps: steps || [] };
        })
      );

      setRuns(runsWithProgress as any);
    } catch (err: any) {
      console.error("Error fetching runs:", err);
      setError(err.message || "Failed to load runs");
      toast({
        variant: "destructive",
        title: "Error loading runs",
        description: err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRuns();
  }, [organizationId]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "COMPLETED":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "IN_PROGRESS":
        return <PlayCircle className="h-4 w-4 text-blue-500" />;
      case "CANCELLED":
        return <XCircle className="h-4 w-4 text-destructive" />;
      default:
        return <Clock className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "COMPLETED":
        return "bg-green-500/10 text-green-500";
      case "IN_PROGRESS":
        return "bg-blue-500/10 text-blue-500";
      case "CANCELLED":
        return "bg-destructive/10 text-destructive";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  const calculateProgress = (steps?: { status: string }[]) => {
    if (!steps || steps.length === 0) return 0;
    const completed = steps.filter(s => s.status === "COMPLETED" || s.status === "SKIPPED").length;
    return Math.round((completed / steps.length) * 100);
  };

  if (loading) {
    return <ListPageSkeleton count={3} />;
  }

  if (error) {
    return (
      <div className="container py-8 px-4">
        <ErrorState 
          title="Failed to load runs" 
          description={error}
          onRetry={fetchRuns}
        />
      </div>
    );
  }

  return (
    <div className="container py-6 md:py-8 space-y-6 px-4 sm:px-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold mb-1 md:mb-2">Runs</h1>
          <p className="text-sm md:text-base text-muted-foreground">
            Active and completed SOP executions
          </p>
        </div>
        <Button onClick={() => setIsStartDialogOpen(true)} className="w-full sm:w-auto gradient-primary hover:opacity-90">
          <Plus className="mr-2 h-4 w-4" />
          Start New Run
        </Button>
      </div>

      {runs.length === 0 ? (
        <EmptyState
          icon={PlayCircle}
          title="No runs yet"
          description="Start your first SOP run to track execution and progress"
          action={{
            label: "Start New Run",
            onClick: () => setIsStartDialogOpen(true),
          }}
        />
      ) : (
        <div className="space-y-4">
          {runs.map((run) => {
            const progress = calculateProgress(run.steps);
            return (
              <Card key={run.id} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(run.status)}
                        <CardTitle className="text-base md:text-lg">{run.name}</CardTitle>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        SOP: {run.sop_template?.title || "Unknown SOP"}
                      </p>
                    </div>
                    <Badge className={getStatusColor(run.status)}>
                      {run.status.replace("_", " ")}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Progress</span>
                      <span className="font-medium">{progress}%</span>
                    </div>
                    <Progress value={progress} />
                  </div>
                  
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-sm">
                    <div className="space-y-1">
                      {run.started_at && (
                        <p className="text-muted-foreground">
                          Started: {format(new Date(run.started_at), "MMM d, yyyy")}
                        </p>
                      )}
                      {run.due_at && (
                        <p className="text-muted-foreground">
                          Due: {format(new Date(run.due_at), "MMM d, yyyy")}
                        </p>
                      )}
                      {run.completed_at && (
                        <p className="text-muted-foreground">
                          Completed: {format(new Date(run.completed_at), "MMM d, yyyy")}
                        </p>
                      )}
                    </div>
                    <Link to={`/app/runs/${run.id}`}>
                      <Button variant="outline" size="sm" className="w-full sm:w-auto">
                        View Details
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <StartRunDialog
        open={isStartDialogOpen}
        onOpenChange={setIsStartDialogOpen}
        organizationId={organizationId}
        userId={user.id}
        onSuccess={fetchRuns}
      />
    </div>
  );
};

export default Runs;
